/** FOCNA teaching age range keys (in-memory filter values). */
export type FocnaAgeKey =
  | 'cuna'
  | 'parvulos'
  | 'primarios'
  | 'principiantes'
  | 'preadolescentes'
  | 'adolescentes';

function toDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  if (typeof (value as { toDate?: () => Date }).toDate === 'function') {
    const d = (value as { toDate: () => Date }).toDate();
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'object' && value !== null && 'seconds' in value) {
    const seconds = Number((value as { seconds: number }).seconds);
    if (!isNaN(seconds)) {
      const d = new Date(seconds * 1000);
      return isNaN(d.getTime()) ? null : d;
    }
  }
  return null;
}

/**
 * FOCNA teaching age bucket from fechaNacimiento.
 * - Cuna: >= 1.5 years until before 3rd birthday
 * - Párvulos: 3 <= years < 6
 * - Primarios: 6 <= years < 8
 * - Principiantes: 8 <= years < 10
 * - Pre adolescentes: 10 <= years < 13
 * - Adolescentes: 13 <= years < 18
 * Returns null if no DOB or outside ranges.
 */
export function getFocnaAgeBucket(
  fechaNacimiento: unknown,
  now: Date = new Date()
): FocnaAgeKey | null {
  const birth = toDate(fechaNacimiento);
  if (!birth || birth > now) return null;

  let years = now.getFullYear() - birth.getFullYear();
  const monthDiff = now.getMonth() - birth.getMonth();
  const dayDiff = now.getDate() - birth.getDate();
  if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
    years--;
  }

  let totalMonths =
    (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) {
    totalMonths--;
  }
  totalMonths = Math.max(0, totalMonths);

  if (totalMonths >= 18 && years < 3) return 'cuna';
  if (years >= 3 && years < 6) return 'parvulos';
  if (years >= 6 && years < 8) return 'primarios';
  if (years >= 8 && years < 10) return 'principiantes';
  if (years >= 10 && years < 13) return 'preadolescentes';
  if (years >= 13 && years < 18) return 'adolescentes';
  return null;
}
